/**
 * TipTap doc JSON → PDF bytes (via pdf-lib).
 *
 * Lightweight typesetter for note exports. Walks the same ProseMirror
 * tree as `noteDocToMarkdown` but lays it out on A4 pages with standard
 * Helvetica fonts: headings, paragraphs, lists, blockquotes, code blocks,
 * horizontal rules, inline marks (bold/italic/code/link).
 *
 * Not a Markdown / HTML renderer — formatting is deliberately simple so
 * we don't pull a heavy HTML-to-PDF dependency. Good enough for notes
 * shared with the team.
 */

import { PDFDocument, type PDFFont, rgb, StandardFonts } from "pdf-lib";

interface PMMark {
  type: string;
  attrs?: Record<string, unknown>;
}
interface PMNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: PMNode[];
  marks?: PMMark[];
  text?: string;
}

/* ─── page metrics (A4 portrait, 1 pt = 1/72 in) ──────────────────── */

const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN = 54; // ≈ 19 mm
const BODY_SIZE = 11;
const BODY_LEADING = 16;
const HEADING_SIZES: Record<number, number> = { 1: 24, 2: 18, 3: 14 };
const CODE_SIZE = 10;
const BLOCK_SPACING = 8;

interface Style {
  bold?: boolean;
  italic?: boolean;
  mono?: boolean;
  link?: string;
  strike?: boolean;
}

interface Run {
  text: string;
  style: Style;
}

interface Fonts {
  regular: PDFFont;
  bold: PDFFont;
  italic: PDFFont;
  boldItalic: PDFFont;
  mono: PDFFont;
}

interface Cursor {
  page: ReturnType<PDFDocument["addPage"]>;
  y: number;
}

/* ─── public API ──────────────────────────────────────────────────── */

export async function noteDocToPdfBytes(doc: unknown, title: string): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const fonts: Fonts = {
    regular: await pdf.embedFont(StandardFonts.Helvetica),
    bold: await pdf.embedFont(StandardFonts.HelveticaBold),
    italic: await pdf.embedFont(StandardFonts.HelveticaOblique),
    boldItalic: await pdf.embedFont(StandardFonts.HelveticaBoldOblique),
    mono: await pdf.embedFont(StandardFonts.Courier),
  };

  let cur: Cursor = {
    page: pdf.addPage([PAGE_W, PAGE_H]),
    y: PAGE_H - MARGIN,
  };

  const contentWidth = PAGE_W - MARGIN * 2;

  // Title block (note name)
  if (title.trim().length > 0) {
    cur = drawHeading(pdf, cur, fonts, 1, title.trim(), contentWidth);
  }

  const root = doc as PMNode | undefined;
  for (const block of root?.content ?? []) {
    cur = renderBlock(pdf, cur, fonts, block, contentWidth, 0);
  }

  return pdf.save();
}

/* ─── block dispatch ──────────────────────────────────────────────── */

function renderBlock(
  pdf: PDFDocument,
  cur: Cursor,
  fonts: Fonts,
  node: PMNode,
  width: number,
  indent: number,
): Cursor {
  switch (node.type) {
    case "paragraph": {
      const runs = collectRuns(node.content ?? [], {});
      if (runs.length === 0 || runs.every((r) => r.text.trim() === "")) {
        return advance(pdf, cur, BLOCK_SPACING);
      }
      return drawWrapped(pdf, cur, fonts, runs, BODY_SIZE, BODY_LEADING, width - indent, indent);
    }
    case "heading": {
      const level = Math.min(3, Math.max(1, Number(node.attrs?.level ?? 1)));
      const text = collectPlain(node.content ?? []);
      return drawHeading(pdf, cur, fonts, level, text, width - indent, indent);
    }
    case "bulletList":
      return renderList(pdf, cur, fonts, node, width, indent, false);
    case "orderedList":
      return renderList(pdf, cur, fonts, node, width, indent, true);
    case "blockquote":
      return renderBlockquote(pdf, cur, fonts, node, width, indent);
    case "codeBlock":
      return renderCodeBlock(pdf, cur, fonts, node, width, indent);
    case "horizontalRule":
      return drawHorizontalRule(pdf, cur, width, indent);
    default:
      // Render children if any, otherwise drop unknown nodes silently.
      if (node.content) {
        let c = cur;
        for (const child of node.content) {
          c = renderBlock(pdf, c, fonts, child, width, indent);
        }
        return c;
      }
      return cur;
  }
}

/* ─── inline runs ─────────────────────────────────────────────────── */

function collectRuns(nodes: PMNode[], parentStyle: Style): Run[] {
  const out: Run[] = [];
  for (const n of nodes) {
    if (n.type === "hardBreak") {
      out.push({ text: "\n", style: parentStyle });
      continue;
    }
    if (n.type === "text") {
      const style = styleFromMarks(n.marks ?? [], parentStyle);
      out.push({ text: n.text ?? "", style });
      continue;
    }
    // Nested inline-ish nodes — recurse.
    if (n.content) out.push(...collectRuns(n.content, parentStyle));
    else if (n.text) out.push({ text: n.text, style: parentStyle });
  }
  return out;
}

function styleFromMarks(marks: PMMark[], base: Style): Style {
  const s: Style = { ...base };
  for (const m of marks) {
    if (m.type === "bold") s.bold = true;
    else if (m.type === "italic") s.italic = true;
    else if (m.type === "strike") s.strike = true;
    else if (m.type === "code") s.mono = true;
    else if (m.type === "link") {
      const href = m.attrs?.href;
      if (typeof href === "string") s.link = href;
    }
  }
  return s;
}

function collectPlain(nodes: PMNode[]): string {
  return collectRuns(nodes, {})
    .map((r) => r.text)
    .join("");
}

function fontFor(fonts: Fonts, style: Style): PDFFont {
  if (style.mono) return fonts.mono;
  if (style.bold && style.italic) return fonts.boldItalic;
  if (style.bold) return fonts.bold;
  if (style.italic) return fonts.italic;
  return fonts.regular;
}

/* ─── layout helpers ──────────────────────────────────────────────── */

function ensureSpace(pdf: PDFDocument, cur: Cursor, needed: number): Cursor {
  if (cur.y - needed >= MARGIN) return cur;
  return {
    page: pdf.addPage([PAGE_W, PAGE_H]),
    y: PAGE_H - MARGIN,
  };
}

function advance(pdf: PDFDocument, cur: Cursor, dy: number): Cursor {
  const next = ensureSpace(pdf, cur, dy);
  return { page: next.page, y: next.y - dy };
}

interface Token {
  text: string;
  style: Style;
  width: number;
  isSpace: boolean;
  forceBreak?: boolean;
}

/** Split runs into per-word tokens preserving style + whitespace. */
function tokenize(runs: Run[], fonts: Fonts, size: number): Token[] {
  const tokens: Token[] = [];
  for (const run of runs) {
    if (run.text === "\n") {
      tokens.push({
        text: "",
        style: run.style,
        width: 0,
        isSpace: false,
        forceBreak: true,
      });
      continue;
    }
    const parts = run.text.split(/(\s+)/);
    for (const part of parts) {
      if (!part) continue;
      const isSpace = /^\s+$/.test(part);
      const font = fontFor(fonts, run.style);
      const width = font.widthOfTextAtSize(part, size);
      tokens.push({ text: part, style: run.style, width, isSpace });
    }
  }
  return tokens;
}

interface LayoutLine {
  tokens: Token[];
  width: number;
}

function wrap(tokens: Token[], maxWidth: number): LayoutLine[] {
  const lines: LayoutLine[] = [];
  let cur: LayoutLine = { tokens: [], width: 0 };
  for (const t of tokens) {
    if (t.forceBreak) {
      lines.push(cur);
      cur = { tokens: [], width: 0 };
      continue;
    }
    if (cur.width + t.width > maxWidth && cur.tokens.length > 0) {
      // Drop trailing space on the broken line.
      while (cur.tokens.length > 0 && cur.tokens[cur.tokens.length - 1]!.isSpace) {
        const dropped = cur.tokens.pop()!;
        cur.width -= dropped.width;
      }
      lines.push(cur);
      cur = { tokens: [], width: 0 };
      if (t.isSpace) continue; // don't lead a new line with a space
    }
    cur.tokens.push(t);
    cur.width += t.width;
  }
  if (cur.tokens.length > 0) lines.push(cur);
  return lines;
}

function drawWrapped(
  pdf: PDFDocument,
  cur: Cursor,
  fonts: Fonts,
  runs: Run[],
  size: number,
  leading: number,
  width: number,
  indent: number,
  color = rgb(0.07, 0.09, 0.12),
): Cursor {
  const tokens = tokenize(runs, fonts, size);
  const lines = wrap(tokens, width);
  let c = cur;
  for (const line of lines) {
    c = ensureSpace(pdf, c, leading);
    let x = MARGIN + indent;
    for (const tk of line.tokens) {
      const font = fontFor(fonts, tk.style);
      c.page.drawText(tk.text, {
        x,
        y: c.y - size,
        size,
        font,
        color: tk.style.link ? rgb(0.15, 0.45, 0.85) : color,
      });
      // Underline for links.
      if (tk.style.link && !tk.isSpace) {
        c.page.drawLine({
          start: { x, y: c.y - size - 1 },
          end: { x: x + tk.width, y: c.y - size - 1 },
          thickness: 0.5,
          color: rgb(0.15, 0.45, 0.85),
        });
      }
      if (tk.style.strike && !tk.isSpace) {
        c.page.drawLine({
          start: { x, y: c.y - size + size * 0.35 },
          end: { x: x + tk.width, y: c.y - size + size * 0.35 },
          thickness: 0.5,
          color,
        });
      }
      x += tk.width;
    }
    c = { page: c.page, y: c.y - leading };
  }
  return advance(pdf, c, BLOCK_SPACING);
}

function drawHeading(
  pdf: PDFDocument,
  cur: Cursor,
  fonts: Fonts,
  level: number,
  text: string,
  width: number,
  indent = 0,
): Cursor {
  const size = HEADING_SIZES[level] ?? BODY_SIZE;
  const leading = Math.round(size * 1.25);
  const runs: Run[] = [{ text, style: { bold: true } }];
  const before = level === 1 ? 12 : 6;
  let c = advance(pdf, cur, before);
  c = drawWrapped(pdf, c, fonts, runs, size, leading, width, indent);
  return c;
}

function renderList(
  pdf: PDFDocument,
  cur: Cursor,
  fonts: Fonts,
  node: PMNode,
  width: number,
  indent: number,
  ordered: boolean,
): Cursor {
  let c = cur;
  const items = node.content ?? [];
  items.forEach((item, i) => {
    const marker = ordered ? `${i + 1}.` : "•";
    const markerWidth = fonts.regular.widthOfTextAtSize(`${marker}  `, BODY_SIZE);
    // Draw marker on the first line.
    c = ensureSpace(pdf, c, BODY_LEADING);
    c.page.drawText(marker, {
      x: MARGIN + indent,
      y: c.y - BODY_SIZE,
      size: BODY_SIZE,
      font: fonts.regular,
      color: rgb(0.4, 0.45, 0.5),
    });
    // Render item content with extra indent so subsequent lines align.
    let inner = c;
    for (const child of item.content ?? []) {
      inner = renderBlock(pdf, inner, fonts, child, width, indent + markerWidth);
    }
    c = inner;
  });
  return c;
}

function renderBlockquote(
  pdf: PDFDocument,
  cur: Cursor,
  fonts: Fonts,
  node: PMNode,
  width: number,
  indent: number,
): Cursor {
  const startY = cur.y;
  let c = cur;
  for (const child of node.content ?? []) {
    c = renderBlock(pdf, c, fonts, child, width, indent + 14);
  }
  // Draw the left bar on the page where the quote BEGAN. If the quote spans
  // multiple pages this only marks the first one — acceptable for v1.
  if (c.page === cur.page) {
    cur.page.drawLine({
      start: { x: MARGIN + indent + 4, y: startY - 2 },
      end: { x: MARGIN + indent + 4, y: c.y + BLOCK_SPACING },
      thickness: 2,
      color: rgb(0.7, 0.75, 0.8),
    });
  }
  return c;
}

function renderCodeBlock(
  pdf: PDFDocument,
  cur: Cursor,
  fonts: Fonts,
  node: PMNode,
  width: number,
  indent: number,
): Cursor {
  const text = (node.content ?? []).map((c) => c.text ?? "").join("");
  const lines = text.split("\n");
  // Each line at code leading; soft-wrap long lines on character boundaries.
  const innerWidth = width - indent - 16;
  const leading = 14;
  const padX = 8;
  const padY = 8;

  // Pre-wrap into display lines.
  const display: string[] = [];
  for (const raw of lines) {
    if (raw.length === 0) {
      display.push("");
      continue;
    }
    let buf = "";
    for (const ch of raw) {
      const test = buf + ch;
      const w = fonts.mono.widthOfTextAtSize(test, CODE_SIZE);
      if (w > innerWidth && buf.length > 0) {
        display.push(buf);
        buf = ch;
      } else {
        buf = test;
      }
    }
    if (buf.length > 0) display.push(buf);
  }
  const blockHeight = display.length * leading + padY * 2;
  const c = ensureSpace(pdf, cur, blockHeight);
  // Background
  c.page.drawRectangle({
    x: MARGIN + indent,
    y: c.y - blockHeight,
    width: width - indent,
    height: blockHeight,
    color: rgb(0.95, 0.96, 0.97),
  });
  let y = c.y - padY;
  for (const line of display) {
    c.page.drawText(line, {
      x: MARGIN + indent + padX,
      y: y - CODE_SIZE,
      size: CODE_SIZE,
      font: fonts.mono,
      color: rgb(0.1, 0.12, 0.16),
    });
    y -= leading;
  }
  const next: Cursor = { page: c.page, y: c.y - blockHeight };
  return advance(pdf, next, BLOCK_SPACING);
}

function drawHorizontalRule(pdf: PDFDocument, cur: Cursor, width: number, indent: number): Cursor {
  const c = advance(pdf, cur, 6);
  c.page.drawLine({
    start: { x: MARGIN + indent, y: c.y },
    end: { x: MARGIN + indent + (width - indent), y: c.y },
    thickness: 0.5,
    color: rgb(0.75, 0.78, 0.82),
  });
  return advance(pdf, c, BLOCK_SPACING + 4);
}
